import { FC } from 'react';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import merge from '~/utils/mergeWithUndefined';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { Form } from '~/components';
import { useCommonErrors, useValidator } from '~/controllers/validation';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import PersonFormItems from '~/pages/ksa/application/managers/components/PersonFormItems';
import { KsaManagers } from '~/types/ksa/KsaManagers';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { WithCurrentUserTag } from '~/components/CurrentUser';
import { isCurrentUserByEmail } from '~/utils/getDecodedJwt';
import { useMobileVerification } from '~/hooks/useMobileVerification';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import { Application, Stage } from '~/types/Application';
import { hasAtLeastOneDeclinedField } from '~/utils/softDeclinesFields';

interface Props extends Partial<FormItemInjectedProps<KsaManagers>> {
  uid: string;
  onClose: () => void;
}

const ManagerEdit: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaManagers>;
  const { t } = useTranslation('ksaApplication');
  const applicationQuery = useGetApplication();
  const managersFormState = { managers: value };

  const currentIndex =
    managersFormState.managers?.managers?.findIndex((item: any) => item?.uid === uid) || 0;

  const mutateMobileVerification = useMobileVerification();

  const [, handleError] = useCommonErrors();

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = merge(managersFormState, newValues);
    const isAuthorizedSignatory =
      updatedData?.managers?.managers[currentIndex]?.personalData?.isAuthorizedSignatory;
    const hasVerifiedPhoneNumber =
      managersFormState?.managers?.managers[currentIndex]?.personalData?.phoneNumber;
    if (isAuthorizedSignatory && !hasVerifiedPhoneNumber) {
      const updatedPhoneNumber =
        updatedData?.managers?.managers[currentIndex]?.personalData?.phoneNumber;
      mutateMobileVerification
        .mutateAsync({ uid, phoneNumber: updatedPhoneNumber })
        .then(() => {
          onChange?.(updatedData.managers);
          onClose();
        })
        .catch((error) => {
          const code = error?.response?.data?.code;
          if (
            code === 'MOBILE_VERIFICATION_INVALID_NUMBER' ||
            code === 'MOBILE_VERIFICATION_INVALID_ID'
          ) {
            handleError.onError([{ name: ['MOBILE'], errors: [] }]);
          }
          if (code === 'MOBILE_VERIFICATION_NOT_OWNER') {
            handleError.onError([{ name: ['MOBILE_NOT_OWNER'], errors: [] }]);
          }
        });
    } else {
      onChange?.(updatedData.managers);
      onClose();
    }
  };

  const managerEmail = managersFormState?.managers?.managers?.find((item) => item?.uid === uid)
    ?.personalData?.email;
  const isCurrent = isCurrentUserByEmail(managerEmail);
  const withContinueButton = !isEditButtonDisabled(
    applicationQuery.data,
    hasAnyDeclinedField(applicationQuery.data)
  );
  return (
    <FormDrawer
      id="managerEditDrawer"
      open={!!uid}
      onClose={onClose}
      title={<WithCurrentUserTag showLabel={isCurrent}>{t('Manager')}</WithCurrentUserTag>}
      formName={managersFormName}
      withErrors
      withContinueButton={withContinueButton}
    >
      <ManagerForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={managersFormState}
        isVerifiedPhone={
          !!(
            managersFormState?.managers?.managers &&
            managersFormState?.managers?.managers[currentIndex]?.personalData?.phoneNumber
          )
        }
      />
    </FormDrawer>
  );
};

interface ManagerFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  isVerifiedPhone: boolean;
  initialValues?: any | undefined;
}

const ManagerForm: FC<ManagerFormProps> = (props) => {
  const { onFinish, initialValues, index, isVerifiedPhone } = props;

  const [form] = Form.useForm<ApplicationPayload>();

  const validatorController = useValidator(form.getFieldsValue(), undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  const managerFromProvided: KsaPerson | undefined = get(
    payload?.provided,
    managersGetFormName(index)
  );
  const managerFromApplication: KsaPerson | undefined = get(
    applicationQuery?.data?.payload,
    managersGetFormName(index)
  );

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: applicationQuery.data?.payload?.errorInfo?.errors,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={managersFormName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <PersonFormItems
          name={managersGetFormName(index)}
          hasPassport={!!managerFromProvided?.passport?.documentNumber}
          personalDocumentType={managerFromProvided?.personalDocument?.idType}
          hasVerifiedPhoneNumber={
            !!managerFromProvided?.personalData?.phoneNumber ||
            !!managerFromApplication?.personalData?.phoneNumber ||
            isVerifiedPhone
          }
          isSignedWithEmail={!!managerFromProvided?.personalData?.email}
          form={form}
        />
      </Form>
    </FormItemContextProvider>
  );
};

export const stepName = 'managers';
export const managersFormName = 'managers';
export const managersGetFormName = (index: number) => [stepName, managersFormName, index];

export const hasAnyDeclinedField = (application: Application | undefined) =>
  hasAtLeastOneDeclinedField(application, stepName);

export default ManagerEdit;
